import { uiLogger, authLogger } from "../../utils/logger"; import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import useAppStore from '../../stores/useAppStore';
import { registrationService } from '../../services/api/registrationService';
import { authApi } from '../../services/api/authApi';
import { authService } from '../../services/api';
import { redirectToApp } from '../../utils/redirectToApp';
import { User } from "@/types";
import { emailService } from "@/services/api";

// Helper function to check if we're in mock mode
const getMockMode = (): boolean => {
  if (typeof window !== 'undefined') {
    if (typeof window.__TEST_MOCK_MODE__ !== 'undefined') {
      return window.__TEST_MOCK_MODE__;
    }

    try {
      const testMode = localStorage.getItem('TEST_MOCK_MODE');
      if (testMode !== null) {
        return testMode === 'true';
      }
    } catch (e) {
      // localStorage might not be available
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mockParam = urlParams.get('mock');
      if (mockParam !== null) {
        return mockParam === 'true';
      }
    } catch (e) {
      // URL parsing might fail
    }
  }

  return import.meta.env.VITE_MOCK_MODE === 'true';
};

const sendEmailWithRegistrationDetails = async (user: User) => {
  // Send registration notification email after successful verification
  try {
    authLogger.info('🔍 RegistrationService: Sending registration notification email');

    // Extract user name from email (fallback if no proper name available)
    const userName = user.name ?? user.email;

    const registrationNotificationData = {
      userEmail: user.email,
      userName: userName,
      timestamp: new Date().toISOString(),
      // Add tenant info if available (you might need to pass this from the calling component)
      tenantName: user.tenantId, // Could be passed as parameter if needed
    };

    const notificationResult = await emailService.sendRegistrationNotification(registrationNotificationData);

    if (notificationResult.success) {
      authLogger.info('📧 Registration notification sent successfully');
    } else {
      authLogger.warn('⚠️ Registration notification failed to send, but continuing with verification');
    }

  } catch (notificationError) {
    // Don't fail the verification if notification email fails
    authLogger.error('❌ Registration notification email failed:', notificationError);
  }
}

export default function EmailVerificationModal() {
  const navigate = useNavigate();
  const {
    isEmailVerificationModalOpen,
    closeEmailVerificationModal,
    openLoginModalClean,
    setPendingEmailVerification,
    setVerificationFromLogin,
    clearRegistrationData,
    registrationData,
    isVerificationFromLogin,
    login
  } = useAppStore();

  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  // Cleanup temporary password when modal closes
  useEffect(() => {
    if (!isEmailVerificationModalOpen) {
      // Clear temporary password when modal is closed (unless verification was successful)
      if (!isVerified) {
        sessionStorage.removeItem('temp_registration_password');
      }
    }
  }, [isEmailVerificationModalOpen, isVerified]);

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setVerificationCode(e.target.value);
    if (error) setError('');
  };


  // Mock auto-login for testing
  const mockAutoLogin = async (): Promise<void> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create mock user data
    const mockUser = {
      userId: 'mock-user-id',
      username: registrationData?.email || 'mock@example.com',
      attributes: {
        email: registrationData?.email || 'mock@example.com',
        email_verified: true,
        'custom:tenantId': registrationData?.tenantId || 'mock-tenant-id',
      }
    };

    // Set mock tenant ID
    const mockTenantId = registrationData?.tenantId || 'mock-tenant-id';
    const mockTenantName = registrationData?.tenantName || 'Mock Company';

    // Update app state with mock user
    login({
      id: mockUser.userId,
      email: mockUser.attributes.email,
      name: 'Unknown', // Set name to Unknown to be consistent
      firstName: 'N/A',
      lastName: 'N/A',
      tenantId: mockTenantId,
      company: 'N/A',
      useIcon: true,
      role: 'Admin'
    });

    // Show redirecting state
    setIsRedirecting(true);

    // Close modal
    closeEmailVerificationModal();

    // Redirect to app with fresh tokens
    await redirectToApp();

    return;
  };

  const handleVerifyEmail = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (!registrationData?.email) {
      setError('Registration data not found. Please start the signup process again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await registrationService.verifyEmail(
        registrationData.email,
        verificationCode.trim()
      );

      if (result.success) {
        setIsVerified(true);
        setPendingEmailVerification(false); // Clear pending verification state
        setVerificationFromLogin(false); // Clear login-specific flag
        toast.success('Email verified successfully!');

        // Auto-login user after successful email verification

        // If in mock mode, use mock auto-login
        if (getMockMode()) {
          await mockAutoLogin();
          return;
        }

        try {
          // Get temporarily stored password
          const tempPassword = sessionStorage.getItem('temp_registration_password');

          if (tempPassword) {
            // Use static import
            const loginResult = await authApi.login(registrationData.email, tempPassword);

            if (loginResult.success) {
              // Clear temporary password
              sessionStorage.removeItem('temp_registration_password');

              // Explicitly update app state with user information
              login(loginResult.user!);

              sendEmailWithRegistrationDetails(JSON.parse(sessionStorage.getItem('user')!))

              // Show redirecting state
              setIsRedirecting(true);

              // Close email verification modal
              closeEmailVerificationModal();

              // Redirect to app with fresh tokens
              await redirectToApp();
            } else {
              toast.error('Please log in to complete your setup.');

              setTimeout(() => {
                closeEmailVerificationModal();
                openLoginModalClean();
              }, 1000);
            }
          } else {
            uiLogger.warn('🔍 EmailVerification: No temporary password found, prompting manual login');
            toast.error('Please log in to complete your setup.');

            setTimeout(() => {
              closeEmailVerificationModal();
              openLoginModalClean();
            }, 1000);
          }
        } catch (loginError) {
          authLogger.error('🔍 EmailVerification: Auto-login error:', loginError);
          toast.error('Please log in to complete your setup.');

          setTimeout(() => {
            closeEmailVerificationModal();
            openLoginModalClean();
          }, 1000);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Email verification failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);

      // If email verification succeeded but tenant creation failed, clear registration data
      // so user doesn't get stuck in verification modal on reload
      if (isVerified) {
        clearRegistrationData();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!registrationData?.email) {
      setError('Registration data not found. Please start the signup process again.');
      return;
    }

    setIsResending(true);
    setError('');

    try {
      const result = await registrationService.resendVerificationCode(registrationData.email);

      if (result.success) {
        toast.success('Verification code sent! Please check your email.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend verification code.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = (): void => {
    closeEmailVerificationModal();
    setVerificationCode('');
    setError('');
    setIsVerified(false);
    setIsRedirecting(false);
    
    // Clear login-specific flags when closing
    setPendingEmailVerification(false);
    setVerificationFromLogin(false);
  };

  const switchToLogin = (): void => {
    closeEmailVerificationModal();
    openLoginModalClean();
  };

  return (
    <Transition show={isEmailVerificationModalOpen}>
      <Dialog onClose={handleClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {isVerified ? 'Email Verified!' : 'Verify Your Email'}
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {isVerified
                        ? isRedirecting
                          ? 'Redirecting to your dashboard...'
                          : 'Setting up your workspace...'
                        : isVerificationFromLogin
                          ? 'Please verify your email to complete login'
                          : 'Check your inbox for the verification code'
                      }
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Success State */}
                {isVerified ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-success-100 dark:bg-success-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckIcon className="h-8 w-8 text-success-600 dark:text-success-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Email Verified Successfully!
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {isRedirecting
                        ? 'Your account is ready. Redirecting to your dashboard...'
                        : 'Your account has been verified. We\'re now setting up your workspace...'
                      }
                    </p>
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        {isRedirecting
                          ? 'Redirecting...'
                          : 'Creating your workspace...'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Email Icon and Info */}
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
                        <EnvelopeIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">
                        We've sent a verification code to:
                      </p>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                        {registrationData?.email}
                      </p>
                      {getMockMode() && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                          Mock mode enabled. Use code: 123456
                        </p>
                      )}
                    </div>

                    {/* Info box for users coming from login */}
                    {isVerificationFromLogin && (
                      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>Account found but not verified:</strong> Your account was created but the email wasn't verified yet. Please enter the verification code to complete your login.
                        </p>
                      </div>
                    )}

                    <form onSubmit={handleVerifyEmail} className="space-y-4">
                      {/* Verification Code Input */}
                      <div>
                        <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Verification Code
                        </label>
                        <input
                          type="text"
                          id="verificationCode"
                          name="verificationCode"
                          value={verificationCode}
                          onChange={handleVerificationCodeChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-lg tracking-widest"
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                          autoComplete="one-time-code"
                        />
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                      )}

                      {/* Verify Button */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Verifying...
                          </>
                        ) : (
                          'Verify Email'
                        )}
                      </button>
                    </form>

                    {/* Resend Code */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {isVerificationFromLogin 
                          ? "Didn't receive the code? We can send it again."
                          : "Didn't receive the code?"
                        }
                      </p>
                      <button
                        onClick={handleResendCode}
                        disabled={isResending}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isResending ? (
                          <>
                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                            Sending...
                          </>
                        ) : (
                          'Resend Code'
                        )}
                      </button>

                      {/* Back to Login */}
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Want to try signing in again?{' '}
                          <button
                            onClick={switchToLogin}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200"
                          >
                            Back to Login
                          </button>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
